import {
  TableCellType,
  TableHeader,
} from "../../../../../components/custom-table/table";
import { formatDate } from "../../../../../utils/formatDate";
import { capitalizeWordsSeparatedBySpace } from "../../../../../utils/stringModifications";
import {
  formatAgenticAuthOnFileRemarksForTable,
  type AgenticAuthOnFileRemarks,
} from "./agenticAuthOnFileRemarks";

export type {
  AgenticAuthOnFileRemarkEntry,
  AgenticAuthOnFileRemarks,
} from "./agenticAuthOnFileRemarks";

export const AGENTIC_AUTH_ON_FILE_UPDATED_AT_DISPLAY_FORMAT =
  "MM/DD/YYYY HH:mm";

/** Firestore field key: trimmed, spaces → underscores, lowercased. */
export const toAgenticAuthOnFilePayerKey = (displayName: string): string =>
  displayName.trim().replace(/\s+/g, "_").toLowerCase();

export type AgenticAuthOnFileRow = {
  id: string;
  payerName: string;
  enabled: boolean;
  agent_mode: boolean;
  updatedAt: string;
  remarks: AgenticAuthOnFileRemarks;
};

const DEFAULT_REMARK_THREAD: AgenticAuthOnFileRemarks = [
  [{ user: "—", updatedAt: "—", remark: "NA" }],
];

const normalizeRemarks = (raw: unknown): AgenticAuthOnFileRemarks => {
  if (!raw) return DEFAULT_REMARK_THREAD;
  if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_REMARK_THREAD;
  if (Array.isArray(raw[0])) {
    return raw as AgenticAuthOnFileRemarks;
  }
  const flat = raw as Record<string, unknown>[];
  const entries = flat.map((e) => ({
    user: String(e.user ?? e.user_name ?? "—"),
    updatedAt: formatDate(
      e.updatedAt ?? e.updated_at,
      AGENTIC_AUTH_ON_FILE_UPDATED_AT_DISPLAY_FORMAT,
      "—",
    ),
    remark: String(e.remark ?? e.text ?? "—"),
  }));
  return [entries];
};

const isPayerMapField = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  ("agent_mode" in (value as object) || "enabled" in (value as object));

/**
 * One Firestore document (`…/payers`) whose top-level fields are payer names
 * and values are maps: `{ agent_mode, enabled, … }`.
 */
export const rowsFromAgenticAuthOnFilePayersDoc = (
  data: Record<string, unknown>,
): AgenticAuthOnFileRow[] => {
  const rows: AgenticAuthOnFileRow[] = [];
  for (const [payerKey, value] of Object.entries(data)) {
    if (payerKey === "id") continue;
    if (!isPayerMapField(value)) continue;
    rows.push({
      id: payerKey,
      payerName: payerKey,
      enabled: Boolean(value.enabled ?? value.live),
      agent_mode: Boolean(value.agent_mode ?? value.agentMode),
      updatedAt: formatDate(
        value.updated_at ?? value.updatedAt,
        AGENTIC_AUTH_ON_FILE_UPDATED_AT_DISPLAY_FORMAT,
        "—",
      ),
      remarks: normalizeRemarks(value.remarks ?? value.remark_history),
    });
  }
  return rows.sort((a, b) =>
    a.payerName.localeCompare(b.payerName, undefined, { sensitivity: "base" }),
  );
};

/** Compares only `enabled` / `agent_mode` per payer id (used for dirty detection). */
export const hasAgenticAuthOnFileToggleChanges = (
  baseline: AgenticAuthOnFileRow[],
  current: AgenticAuthOnFileRow[],
): boolean => {
  if (baseline.length !== current.length) return true;
  const baseById = new Map(baseline.map((r) => [r.id, r]));
  for (const row of current) {
    const b = baseById.get(row.id);
    if (!b || b.enabled !== row.enabled || b.agent_mode !== row.agent_mode) {
      return true;
    }
  }
  return false;
};

export const getAgenticAuthOnFileChangedToggleRows = (
  baseline: AgenticAuthOnFileRow[],
  current: AgenticAuthOnFileRow[],
): AgenticAuthOnFileRow[] => {
  const baseById = new Map(baseline.map((r) => [r.id, r]));
  return current.filter((row) => {
    const b = baseById.get(row.id);
    return !b || b.enabled !== row.enabled || b.agent_mode !== row.agent_mode;
  });
};

export enum AgenticAuthOnFileColumnKeys {
  PayerName = "payer_name",
  Live = "live",
  AgentMode = "agent_mode",
  UpdatedAt = "updated_at",
  Remarks = "remarks",
}

export const AgenticAuthOnFileTableHeaders: TableHeader[] = [
  {
    label: "Payer Name",
    key: AgenticAuthOnFileColumnKeys.PayerName,
    order: 1,
    width: 34,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Live",
    key: AgenticAuthOnFileColumnKeys.Live,
    order: 2,
    width: 14,
    sortable: true,
    type: TableCellType.TOGGLE_SWITCH,
  },
  {
    label: "Agent Mode",
    key: AgenticAuthOnFileColumnKeys.AgentMode,
    order: 3,
    width: 14,
    sortable: true,
    type: TableCellType.TOGGLE_SWITCH,
  },
  {
    label: "Updated at",
    key: AgenticAuthOnFileColumnKeys.UpdatedAt,
    order: 4,
    width: 18,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Remarks",
    key: AgenticAuthOnFileColumnKeys.Remarks,
    order: 5,
    width: 20,
    sortable: true,
    type: TableCellType.STRING,
  },
];

export const generateAgenticAuthOnFileTableData = (
  rows: AgenticAuthOnFileRow[],
) => {
  return rows.map((row) => ({
    [AgenticAuthOnFileColumnKeys.PayerName]: capitalizeWordsSeparatedBySpace(
      row.payerName.replace(/_/g, " "),
    ),
    [AgenticAuthOnFileColumnKeys.Live]: {
      id: `${row.id} live`,
      defaultChecked: row.enabled,
    },
    [AgenticAuthOnFileColumnKeys.AgentMode]: {
      id: `${row.id} agent_mode`,
      defaultChecked: row.agent_mode,
    },
    [AgenticAuthOnFileColumnKeys.UpdatedAt]: row.updatedAt,
    [AgenticAuthOnFileColumnKeys.Remarks]:
      formatAgenticAuthOnFileRemarksForTable(row.remarks),
    rowData: row,
  }));
};
/**
 * Inverse of {@link generateAgenticAuthOnFileTableData} toggle `id`s: `"<rowId> live"` | `"<rowId> agent_mode"`.
 * Last space-separated token is the column; the rest is the row id. Unknown shapes → `field: null`, `rowId` unchanged.
 */
export const parseAgenticAuthToggleRowId = (compositeId: string) => {
  const parts = compositeId.split(" ");
  const field = parts.pop();
  if (field !== "live" && field !== "agent_mode") {
    return { rowId: compositeId, field: null as "live" | "agent_mode" | null };
  }
  const rowId = parts.join(" ");
  return { rowId, field };
};
